'use no memo'

import { useMailingLists } from '@gozoki/api'
import type { MailingList } from '@gozoki/api-types'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { Eye } from 'lucide-react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTable, AppTableData } from '~/components/new/app-ui/table/app-table'
import { Title } from '~/components/new/app-ui/title'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { Button } from '~/components/ui/button'
import { useAppTable } from '~/hooks/use-app-table'
import type { PermissionTuple } from '~/utils/ability'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'

const READ: PermissionTuple = ['read', 'mailing-lists.mailing-lists.mailing-list']

export const Route = createFileRoute('/_authenticated/mailing-lists/')({
  staticData: {
    permission: READ,
  },
  component: MailingListsPage,
})
withRoutePermissions(Route)

const columnHelper = createColumnHelper<MailingList>()

const mailingListsColumns = [
  columnHelper.accessor('name', {
    header: 'Nom',
  }),

  columnHelper.accessor('description', {
    header: 'Description',
    cell: ({ getValue }) => getValue() ?? '—',
  }),

  columnHelper.accessor('recipientsCount', {
    header: 'Destinataires',
    size: 140,
  }),

  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 80,
    cell: ({ row }) => {
      const navigate = useNavigate()
      return (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/mailing-lists/$key', params: { key: row.original.key } })}
        >
          <Eye />
        </Button>
      )
    },
  }),
]

function MailingListsPage() {
  // Le `staticData.permission` ne discrimine rien tant que le flag `permission-v2` est OFF :
  // le droit v1 reste la seule garde effective de la page.
  const canRead = useMigratedRight(READ, ['SUPER_ADMIN'])
  const { data: mailingLists = [], isLoading } = useMailingLists()

  const table = useAppTable({
    columns: mailingListsColumns,
    data: mailingLists,
  })

  if (!canRead) {
    return <RbacAccessDenied />
  }

  return (
    <>
      <AppHeader>
        <Title>Listes de diffusion</Title>
      </AppHeader>

      <AppContainer className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Listes de diffusion</h2>
          <p className="text-muted-foreground text-sm">
            Gestion des destinataires des emails automatiques envoyés par la plateforme.
          </p>
        </div>

        <AppTable>
          <AppTableData table={table} />
        </AppTable>

        {isLoading && <p className="text-muted-foreground text-center text-sm">Chargement…</p>}
      </AppContainer>
    </>
  )
}
